// "How to read this": what a dashboard holds, what to do on it and what its words mean,
// in a panel on the right so the dashboard stays in view beside it.
//
// The words are the catalogue's (navigation/catalog) and are never written here, so the
// panel, the dashboard's card on the home screen and page search cannot disagree.
import { useEffect, useId, useRef, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { DASHBOARD_INFO } from '../../navigation/catalog';
import { dashLabel, type DashTab } from '../../navigation/dashboards';

/** What the keyboard can land on inside the panel. */
const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

const HEADING = 'text-[11px] font-semibold uppercase tracking-wide text-gray-400';

interface HowToReadPanelProps {
  tab: DashTab;
  /** Close button, Escape and a click outside the panel all end here. The opener puts the focus back on its button. */
  onClose: () => void;
}

export default function HowToReadPanel({ tab, onClose }: HowToReadPanelProps): ReactElement {
  const info = DASHBOARD_INFO[tab];
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // The panel is drawn at the end of the page, so the focus has to be carried into it.
  useEffect(() => { closeRef.current?.focus(); }, []);

  // Escape closes. Tab stays inside: what is behind the panel is covered and must not be
  // reachable by keyboard while it is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const stops = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || !panelRef.current?.contains(at))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (at === last || !panelRef.current?.contains(at))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[110]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col gap-5 overflow-y-auto border-l border-gray-100 bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <h2 id={titleId} className="min-w-0 break-words text-lg font-semibold text-gray-900">How to read {dashLabel(tab)}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="ml-auto flex-shrink-0 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm font-medium text-gray-700">{info.question}</p>
        <p className="text-sm text-gray-500">{info.purpose}</p>

        <section>
          <h3 className={HEADING}>What to do here</h3>
          <ol className="mt-2 space-y-2">
            {info.howTo.map((step, index) => (
              <li key={step} className="flex gap-2.5 text-sm text-gray-700">
                {/* The list itself says "1, 2, 3" to a screen reader; the badge is for the eye. */}
                <span aria-hidden className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-bold text-primary-700">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <h3 className={HEADING}>Words used on this page</h3>
          <dl className="mt-2 space-y-3">
            {info.glossary.map(([term, meaning]) => (
              <div key={term}>
                <dt className="text-sm font-semibold text-gray-900">{term}</dt>
                <dd className="text-sm text-gray-600">{meaning}</dd>
              </div>
            ))}
          </dl>
        </section>
      </aside>
    </div>,
    document.body,
  );
}
